import { redirect, type RouteObject } from 'react-router-dom'

import { detectBrowserLanguage } from '@/lib/i18n/settings'

// Layouts — JSX lives in dedicated .tsx files so this route table stays JSX-free.
import { ProtectedAccountLayout } from '../layouts/account'
import { AppointmentLayout, ProtectedAppointmentActionLayout } from '../layouts/appointment'
import { AuthLayout } from '../layouts/auth'
import { LanguageLayout } from '../layouts/language'
import { MarketingLayout } from '../layouts/marketing'

// Pages
import { CompleteProfilePage } from '@/_pages/auth/complete-profile'
import { ConsultationsPage } from '@/_pages/account/consultations'
import { ConsultationPage } from '@/_pages/account/consultation'
import { ConsultationLivePage } from '@/_pages/account/consultation-live'
import { ConsultationWaitingPage } from '@/_pages/account/consultation-waiting'
import { DocumentsPage } from '@/_pages/account/documents'
import { EthicsPage } from '@/_pages/account/ethics'
import { ExpertisePage } from '@/_pages/account/expertise'
import { OrderPage } from '@/_pages/account/order'
import { ProfilePage } from '@/_pages/account/profile'
import { EstablishmentPage } from '@/_pages/appointment/establishment'
import { HomePage } from '@/_pages/home'
import { LoginPage } from '@/_pages/auth/login'
import { BookingPage, BookingPaymentCancelPage, BookingPaymentSuccessPage } from '@/_pages/appointment/booking'
import { MeetingLinkPage } from '@/_pages/appointment/meeting-link'
import { RegisterPage } from '@/_pages/auth/register'
import { ReportLostDevicePage } from '@/_pages/auth/report-lost-device'
import { TermsAndConditionsPage } from '@/_pages/terms-and-conditions'
import { AccessibilityPage } from '@/_pages/accessibility'
import { HelpPage } from '@/_pages/help'
import { OrderTrackingPage } from '@/_pages/order-tracking'
import { SitemapPage } from '@/_pages/sitemap'
import { DashboardPage } from '@/_pages/account/dashboard'
import { ExpertisesPage } from '@/_pages/account/expertises'
import { OrdersPage } from '@/_pages/account/orders'

// Loader-based redirects keep this table JSX-free (no <Navigate> elements). They run
// before render, so the redirect resolves without flashing an intermediate component.
const redirectToBrowserLanguage = () => redirect(`/${detectBrowserLanguage()}`)
const redirectHome = () => redirect('/')

export const routes: RouteObject[] = [
  {
    // Root route has no Component: react-router renders an <Outlet /> by default.
    path: '/',
    children: [
      { path: '', loader: redirectToBrowserLanguage },
      {
        path: ':lang',
        Component: LanguageLayout,
        children: [
          {
            Component: MarketingLayout,
            children: [{ path: '', Component: HomePage }],
          },
          { path: 'sitemap', Component: SitemapPage },
          { path: 'accessibility', Component: AccessibilityPage },
          { path: 'help', Component: HelpPage },
          {
            // Explicit static route so legal links don't fall through to the
            // dynamic :establishment route (which would dump a raw CMS error).
            path: 'terms-and-conditions',
            Component: TermsAndConditionsPage,
          },
          {
            Component: AuthLayout,
            children: [
              {
                // Post-login profile completion (gated before a France video consult). Must be an
                // explicit route or the readiness-gate redirect falls through to :establishment.
                path: 'complete-profile',
                Component: CompleteProfilePage,
              },
              { path: 'login', Component: LoginPage },
              { path: 'register', Component: RegisterPage },
              {
                // IEU.07 Cas 1 — self-service lost/stolen device declaration
                path: 'report-lost-device',
                Component: ReportLostDevicePage,
              },
            ],
          },
          {
            path: 'account',
            Component: ProtectedAccountLayout,
            children: [
              { path: '', loader: () => redirect('/account/dashboard') },
              { path: 'dashboard', Component: DashboardPage },
              { path: 'profile', Component: ProfilePage },
              {
                path: 'orders',
                children: [
                  { path: '', Component: OrdersPage },
                  { path: ':referenceId', Component: OrderPage },
                ],
              },
              { path: 'documents', Component: DocumentsPage },
              {
                path: 'consultations',
                children: [
                  { path: '', Component: ConsultationsPage },
                  { path: ':consultationId', Component: ConsultationPage },
                  { path: ':consultationId/waiting', Component: ConsultationWaitingPage },
                  { path: ':consultationId/live', Component: ConsultationLivePage },
                ],
              },
              {
                path: 'expertises',
                children: [
                  { path: '', Component: ExpertisesPage },
                  { path: ':id', Component: ExpertisePage },
                ],
              },
              { path: 'ethics', Component: EthicsPage },
            ],
          },
          {
            Component: AppointmentLayout,
            children: [
              {
                path: 'meeting-links',
                children: [
                  { path: '', loader: redirectHome },
                  {
                    path: ':meetingLink',
                    children: [
                      { path: '', Component: MeetingLinkPage },
                      {
                        path: 'booking',
                        children: [
                          { path: '', Component: BookingPage },
                          {
                            path: 'payment',
                            children: [
                              { path: 'success', Component: BookingPaymentSuccessPage },
                              { path: 'cancel', Component: BookingPaymentCancelPage },
                            ],
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
              {
                path: 'order-tracking',
                Component: ProtectedAppointmentActionLayout,
                children: [
                  { path: '', loader: redirectHome },
                  { path: ':referenceId', Component: OrderTrackingPage },
                ],
              },
              {
                path: ':establishment',
                children: [{ path: '', Component: EstablishmentPage }],
              },
            ],
          },
          { path: '*', loader: redirectHome },
        ],
      },
      { path: '*', loader: redirectToBrowserLanguage },
    ],
  },
]
